'use client';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { AiOutlineCheck } from '@react-icons/all-files/ai/AiOutlineCheck';
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose';
import { MdModeEdit } from '@react-icons/all-files/md/MdModeEdit';
import { FormProvider, useForm } from 'react-hook-form';
import { InputTextHookForm } from '../input-text/hook-form';
import {
  UpdateScreenerNameSchemaType,
  updateScreenerNameSchema,
} from './schema';
import { Skeleton } from '@mui/material';

export const EditableInputName = ({
  formName,
  loading,
  currentName,
  onChangeAssetListName,
  error,
  disabled = false,
}: EditableNameProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const formMethods = useForm<UpdateScreenerNameSchemaType>({
    resolver: zodResolver(updateScreenerNameSchema),
    defaultValues: { name: currentName },
  });

  useEffect(() => {
    if (!!currentName) {
      formMethods.setValue('name', currentName);
    }
  }, [currentName]);

  const onSubmit = (data: UpdateScreenerNameSchemaType) => {
    onChangeAssetListName(data.name);
    setIsEditing(false);
  };

  const actions = [
    {
      icon: <AiOutlineCheck />,
      handler: formMethods.handleSubmit(onSubmit),
    },
    {
      icon: <AiOutlineClose />,
      handler: () => {
        setIsEditing(false), formMethods.setValue('name', currentName!);
      },
    },
  ];

  if (loading) {
    return (
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="h2">
          <Skeleton width={200} />
        </Typography>
      </Stack>
    );
  }

  if (isEditing && formName) {
    return (
      <Stack direction="row" gap={1} alignItems="center">
        <InputTextHookForm
          id="editable_input_name"
          name={formName!}
          isEditing={isEditing}
          actions={actions}
          autoFocus
        />
      </Stack>
    );
  }

  if (isEditing) {
    return (
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Stack direction="row" gap={1} alignItems="center">
            <InputTextHookForm
              name="name"
              autoFocus
              isEditing={isEditing}
              actions={actions}
            />
          </Stack>
        </form>
      </FormProvider>
    );
  }

  return (
    <Stack>
      <Stack
        direction="row"
        gap={2.5}
        alignItems="center"
        onClick={!disabled ? () => setIsEditing(true) : undefined}
        sx={{ ':hover': { cursor: 'pointer' } }}
      >
        <Typography
          variant="h2"
          fontWeight={700}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {currentName}
        </Typography>
        {!currentName && (
          <Typography variant="h2" fontWeight={700} sx={{ opacity: 0.5 }}>
            Digite um nome
          </Typography>
        )}
        {!disabled && (
          <IconButton
            id="editable_input_name_button"
            sx={{
              border: '1px solid #E3E3E3',
              borderRadius: 1,
            }}
          >
            <MdModeEdit size={17} />
          </IconButton>
        )}
      </Stack>
      {error && (
        <Box pl={3}>
          <Typography variant="caption" color="error">
            {error.message}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

interface EditableNameProps {
  loading?: boolean;
  formName?: string;
  error?: any;
  currentName?: string;
  onChangeAssetListName: (value: string) => void;
  disabled?: boolean;
}
